/**
 * ConfirmModal Component
 * A specialized modal for confirmation actions (delete, destructive operations)
 * Built on top of the base Modal component with predefined styling and behaviors
 */

import React from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Icon } from './Icon';
import styled, { css } from 'styled-components';

// ============================================================================
// Styled Components
// ============================================================================

const WarningIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  background: var(--danger-50, #fef2f2);
  border-radius: 50%;
  margin: 0 auto 1rem;
  color: var(--danger-600, #dc2626);
  font-size: 1.25rem;
`;

const Content = styled.div`
  text-align: center;
  padding: 1rem 0;
`;

const Message = styled.p`
  margin: 0 0 1.5rem 0;
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;

  @media (max-width: 480px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const LoadingButton = styled(Button)<{ $isDanger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${(props) =>
    props.$isDanger &&
    css`
      background-color: var(--action-danger);
      border-color: var(--action-danger);
      color: white;

      &:hover:not(:disabled) {
        background-color: var(--action-danger-hover);
        border-color: var(--action-danger-hover);
      }

      &:focus {
        box-shadow: 0 0 0 2px var(--danger-100, #fecaca);
      }
    `}
`;

// ============================================================================
// Component Interface
// ============================================================================

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: 'danger' | 'warning';
  'data-testid'?: string;
}

// ============================================================================
// ConfirmModal Component
// ============================================================================

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = '¿Confirmar acción?',
  message = 'Esta acción no se puede deshacer.',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  isLoading = false,
  variant = 'danger',
  'data-testid': dataTestId,
}) => {
  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="sm"
      title={title}
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      data-testid={dataTestId || 'confirm-modal'}
    >
      <Content>
        <WarningIcon>
          <Icon
            name={variant === 'danger' ? 'delete' : 'warning'}
            size="md"
            aria-hidden
          />
        </WarningIcon>

        <Message>{message}</Message>

        <Actions>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
            data-testid="confirm-modal-cancel"
          >
            {cancelText}
          </Button>
          <LoadingButton
            variant={variant === 'danger' ? 'secondary' : 'primary'}
            $isDanger={variant === 'danger'}
            onClick={handleConfirm}
            disabled={isLoading}
            data-testid="confirm-modal-confirm"
          >
            {isLoading && <Icon name="refresh" size="sm" />}
            {confirmText}
          </LoadingButton>
        </Actions>
      </Content>
    </Modal>
  );
};

export default ConfirmModal;
